interface ReadinessRingProps {
  /** 0–100, or null when there is nothing to measure yet. */
  score: number | null;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

/**
 * A progress ring with the percentage in the middle. An event with nothing
 * to measure shows a dash rather than 0%, which would read as "failing".
 */
export function ReadinessRing({
  score,
  size = 44,
  strokeWidth,
  className = '',
}: ReadinessRingProps) {
  const stroke = strokeWidth ?? Math.max(4, Math.round(size / 9));
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = score == null ? 0 : (Math.min(100, Math.max(0, score)) / 100) * circumference;
  const label = score == null ? 'Readiness not measured yet' : `${score}% ready`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={label}
      className={`flex-shrink-0 ${className}`}
    >
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1eff5" strokeWidth={stroke} />
      {score != null && (
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${filled} ${circumference}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          className="text-primary-600 transition-[stroke-dasharray] duration-500"
        />
      )}
      <text
        x="50%"
        y="50%"
        dominantBaseline="central"
        textAnchor="middle"
        fontSize={Math.round(size / 4)}
        fontWeight={700}
        fill="#1c1a24"
      >
        {score == null ? '—' : `${score}%`}
      </text>
    </svg>
  );
}
